// studio/simEngine.js — Moteur de simulation : code, terminal, diff, tests, agents (SIMULÉ, piloté par le plan LLM réel).
const seed = (s) => String(s).split("").reduce((a, c) => a + c.charCodeAt(0), 7);

export const AGENTS = [
  { name: "Architect AI", task: "Analyse de la structure", dur: "12s", at: 18 },
  { name: "Developer AI", task: "Génération du code", dur: "28s", at: 48 },
  { name: "UI AI", task: "Respect du design AYA", dur: "18s", at: 72 },
  { name: "QA AI", task: "Tests et validation", dur: "22s", at: 94 },
  { name: "Media AI", task: "(optionnel · vidéo)", dur: "—", at: 999, optional: true },
];

const PHASE_KEY = "aya_studio_phases";
export const getLocalPhase = (id) => { try { return JSON.parse(localStorage.getItem(PHASE_KEY) || "{}")[id] || null; } catch { return null; } };
export const setLocalPhase = (id, p) => { const m = JSON.parse(localStorage.getItem(PHASE_KEY) || "{}"); m[id] = p; localStorage.setItem(PHASE_KEY, JSON.stringify(m)); };
export function effectivePhase(build) {
  if (!build) return null;
  if (["deployed", "denied", "rejected", "validation"].includes(build.status)) return build.status;
  return getLocalPhase(build.build_id) || "plan";
}

const CRED_KEY = "aya_studio_credits";
export const getCredits = () => { const v = Number(localStorage.getItem(CRED_KEY)); return Number.isFinite(v) && v > 0 ? v : 12450; };
export const spendCredits = (n) => { const v = Math.max(0, getCredits() - n); localStorage.setItem(CRED_KEY, String(v)); return v; };
export const CRED_MAX = 50000;

export function synthCode(file, plan) {
  if (plan.code_preview?.path === file.path && plan.code_preview.code) return plan.code_preview.code;
  const name = file.path.split("/").pop();
  const base = name.replace(/\.[^.]+$/, "");
  const comp = base.replace(/[^A-Za-z0-9]/g, "");
  const C = comp.charAt(0).toUpperCase() + comp.slice(1);
  if (/\.css$/.test(name)) return `/* ${file.path} — ${file.description} */\n.${base} {\n  display: flex;\n  gap: 12px;\n  border-radius: 14px;\n  background: var(--aya-card);\n  border: 1px solid var(--aya-border);\n}\n.${base}:hover {\n  transform: translateY(-2px);\n  transition: transform 0.2s ease;\n}\n.${base}__title {\n  font-weight: 800;\n  color: var(--aya-text);\n}`;
  if (/\.(ts|js)$/.test(name) && /data|config|store|util/i.test(file.path)) {
    const v = base.replace(/[^A-Za-z0-9]/g, "_");
    return `// ${file.path} — ${file.description}\nexport const ${v} = [\n  { id: "item-1", actif: true, ordre: 1 },\n  { id: "item-2", actif: true, ordre: 2 },\n];\n\nexport function get${C}() {\n  return ${v}.filter((i) => i.actif);\n}\n\nexport function add${C}(item) {\n  ${v}.push({ ...item, actif: true });\n  return ${v}.length;\n}`;
  }
  return `// ${file.path} — ${file.description}\nimport { Card, CardContent } from "@/components/ui/card";\n\nexport default function ${C || "Composant"}() {\n  return (\n    <Card data-testid="${base.toLowerCase().replace(/[^a-z0-9]+/g, "-")}" className="aya-card">\n      <CardContent className="flex flex-col gap-2">\n        {/* ${plan.title} */}\n        <h3 className="font-bold">${plan.title}</h3>\n        <p className="text-sm text-muted">${(plan.summary || "").slice(0, 80)}</p>\n      </CardContent>\n    </Card>\n  );\n}`;
}

export function buildArtifacts(build) {
  if (!build?.plan) return null;
  const plan = build.plan;
  const files = (plan.files || []).map((f) => {
    const s = seed(f.path);
    const isNew = f.action === "créer";
    return { ...f, name: f.path.split("/").pop(), code: synthCode(f, plan), added: isNew ? 40 + (s % 60) : 5 + (s % 20), removed: isNew ? 0 : s % 6, isNew };
  });
  const nTests = 8 + (seed(build.build_id) % 8);
  return {
    files, nTests,
    terminal: [
      "$ yarn dev", "VITE v5.4.1  ready in 324 ms", "➜  Local:   http://localhost:3000/", "➜  Network: http://192.168.1.10:3000/",
      `AYA Studio · analyse de ${files.length} fichier(s)…`,
      ...files.map((f) => `✓ ${f.isNew ? "Composant créé" : "Fichier mis à jour"} · ${f.path}`),
      `✓ Tests UI : ${nTests}/${nTests} réussis`, "✓ Build prêt — en attente de validation JSONFIRST",
    ],
    git: ["$ git add -A", `$ git commit -m "feat: ${plan.title}"`, `[aya-main ${build.build_id.slice(4, 11)}] feat: ${plan.title}`, ` ${files.length} fichier(s) modifié(s), +${files.reduce((a, f) => a + f.added, 0)} insertions, -${files.reduce((a, f) => a + f.removed, 0)} suppressions`],
    tests: [...(plan.tests || []).map((t) => `✓ PASS  ${t}`), `✓ PASS  Rendu responsive mobile / tablette / desktop`, `✓ PASS  data-testid présents sur les éléments interactifs`, "", `Tests : ${nTests} réussis, 0 échec · Durée 4.2s`],
    buildLog: ["$ yarn build", "Creating an optimized production build…", `✓ Compiled successfully (${files.length} module(s) impactés)`, "File sizes after gzip :  187.2 kB  build/static/js/main.js", "Build terminé — artefact prêt pour le pipeline AYA."],
    deployLog: (phase) => phase === "deployed"
      ? ["Pipeline AYA · déploiement validé par le Founder", "✓ JSONFIRST policy : APPROVED", "✓ Publication sur l'environnement cible", "✓ Déploiement terminé (SIMULÉ)"]
      : phase === "validation"
        ? ["Pipeline AYA · validation JSONFIRST en cours…", "→ Approbation Founder requise (voir Approvals)", "Le déploiement s'exécutera après validation."]
        : phase === "denied"
          ? ["⛔ JSONFIRST policy : DENIED", "Le déploiement a été bloqué par une règle de gouvernance."]
          : ["En attente — acceptez les modifications puis cliquez sur Déployer."],
  };
}

export function newComponentLabel(build) {
  if (!build?.plan) return "Nouveau composant";
  const f = (build.plan.files || []).find((x) => x.action === "créer");
  if (f) {
    const base = f.path.split("/").pop().replace(/\.[^.]+$/, "").replace(/([a-z])([A-Z])/g, "$1 $2");
    return base.charAt(0).toUpperCase() + base.slice(1);
  }
  return build.plan.title;
}
